import React, { useEffect, useRef, useState } from 'react';
import { Box, Text, Icon, HStack, useColorModeValue } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { 
  FaChartBar, 
  FaBalanceScale, 
  FaCalendarAlt, 
  FaDollarSign, 
  FaBook, 
  FaChartLine,
  FaSmile,
  FaBullseye,
  FaAd,
  FaFileAlt,
  FaKey,
  FaRocket,
  FaSearch,
  FaTrophy
} from 'react-icons/fa';

// Icon mapping for each dashboard
const iconMap = {
  'Localytics Ranking': FaChartBar,
  'Compare Businesses': FaBalanceScale,
  'Month in Review': FaCalendarAlt,
  'Pricing Analysis': FaDollarSign,
  'Reading List': FaBook,
  'Review Frequency': FaChartLine,
  'Review Growth': FaChartBar,
  'Sentiment': FaSmile,
  'Actions': FaRocket,
  'Set Goals': FaBullseye,
  'Google Ads': FaAd,
  'Content': FaFileAlt,
  'Keywords': FaKey,
};

// Pastel color schemes for each bubble
const bubbleColors = {
  'Localytics Ranking': { bg: '#F3E8FF', border: '#D8B4FE', text: '#7C3AED', icon: '#7C3AED' },
  'Compare Businesses': { bg: '#E0F2FE', border: '#7DD3FC', text: '#0891B2', icon: '#0891B2' },
  'Month in Review': { bg: '#D1FAE5', border: '#6EE7B7', text: '#059669', icon: '#059669' },
  'Pricing Analysis': { bg: '#DCFCE7', border: '#86EFAC', text: '#16A34A', icon: '#16A34A' },
  'Reading List': { bg: '#FEF3C7', border: '#FCD34D', text: '#D97706', icon: '#D97706' },
  'Review Frequency': { bg: '#CFFAFE', border: '#67E8F9', text: '#0891B2', icon: '#0891B2' },
  'Review Growth': { bg: '#FCE7F3', border: '#F9A8D4', text: '#DB2777', icon: '#DB2777' },
  'Sentiment': { bg: '#FEE2E2', border: '#FCA5A5', text: '#DC2626', icon: '#DC2626' },
  'Actions': { bg: '#FEE2E2', border: '#FCA5A5', text: '#DC2626', icon: '#DC2626' },
  'Set Goals': { bg: '#D1FAE5', border: '#6EE7B7', text: '#059669', icon: '#059669' },
  'Google Ads': { bg: '#E0F2FE', border: '#7DD3FC', text: '#0891B2', icon: '#0891B2' },
  'Content': { bg: '#F3E8FF', border: '#D8B4FE', text: '#7C3AED', icon: '#7C3AED' },
  'Keywords': { bg: '#FEF3C7', border: '#FCD34D', text: '#D97706', icon: '#D97706' },
};

// Center node colors for each variant
const centerColors = {
  analyze: { bg: '#EEF2FF', border: '#A5B4FC', text: '#4F46E5', icon: '#4F46E5' },
  win: { bg: '#FDF2F8', border: '#F9A8D4', text: '#DB2777', icon: '#DB2777' },
};

// Center node labels and icons
const centerConfig = {
  analyze: { label: 'Analyze', icon: FaSearch },
  win: { label: 'Win', icon: FaTrophy },
};

/**
 * DashboardLinkBubbles - Mind map style component that connects dashboard links to a central node
 * 
 * Features a central dashboard name with connecting lines to each dashboard link
 * 
 * @param {Array} links - Array of link objects with { label, path }
 * @param {string} variant - 'analyze' or 'win' for center node theme
 */
const DashboardLinkBubbles = ({ links = [], variant = 'analyze' }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [rotationAngle, setRotationAngle] = useState(0);
  
  // Container background
  const containerBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const lineColor = useColorModeValue('#CBD5E1', '#4A5568');

  // Update container size on mount and resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();
    const timer = setTimeout(updateSize, 100);
    window.addEventListener('resize', updateSize);
    
    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timer);
    };
  }, []);

  // Slow rotation animation - completes one full rotation every 120 seconds
  useEffect(() => {
    let animationFrameId;
    let lastTime = performance.now();
    
    const animate = (currentTime) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      
      // Rotate at a rate of (2 * PI) / 120000ms = one full rotation per 120 seconds
      const rotationSpeed = (Math.PI * 2) / 120000; // radians per millisecond
      setRotationAngle(prev => (prev + rotationSpeed * deltaTime) % (Math.PI * 2));
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animationFrameId = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  // Get bubble colors based on label (supports branded variants like "Acme Ranking")
  const getBubbleColors = (label) => {
    if (bubbleColors[label]) return bubbleColors[label];
    if (label.endsWith(' Ranking')) return bubbleColors['Localytics Ranking'];
    return { bg: '#F3E8FF', border: '#D8B4FE', text: '#7C3AED', icon: '#7C3AED' };
  };

  // Get icon for bubble (supports branded variants like "Acme Ranking")
  const getBubbleIcon = (label) => {
    if (iconMap[label]) return iconMap[label];
    if (label.endsWith(' Ranking')) return iconMap['Localytics Ranking'];
    return FaChartBar;
  };

  const handleBubbleClick = (path) => {
    navigate(path);
  };

  // Calculate positions for mind map layout with tilted orbital rotation
  const calculatePositions = () => {
    const { width, height } = containerSize;
    if (width === 0 || height === 0) return { center: { x: 0, y: 0 }, nodes: [] };

    const centerX = width / 2;
    const centerY = height / 2;
    
    // Calculate radius based on container size (with some padding)
    const radiusX = Math.min(width * 0.38, 180);
    const radiusY = Math.min(height * 0.35, 100);
    
    // Tilt angle for the orbital plane (in radians) - tilts the orbit toward viewer
    const tiltAngle = Math.PI * 0.15; // ~27 degrees tilt
    
    // Distribute nodes evenly around the center with rotation offset
    const nodes = links.map((link, index) => {
      const baseAngle = (index / links.length) * Math.PI * 2 - Math.PI / 2; // Start from top
      const angle = baseAngle + rotationAngle; // Add rotation offset
      
      // 3D position on tilted orbital plane
      const x = centerX + Math.cos(angle) * radiusX;
      // Y combines the ellipse Y with a tilt effect based on depth (cos gives depth)
      const depthFactor = Math.cos(angle); // -1 (back) to 1 (front)
      const y = centerY + Math.sin(angle) * radiusY * Math.cos(tiltAngle) - depthFactor * radiusY * Math.sin(tiltAngle) * 0.5;
      
      // Scale based on depth - nodes at "back" are slightly smaller
      const scale = 0.85 + 0.15 * ((depthFactor + 1) / 2); // 0.85 to 1.0
      
      // Z-index based on depth - nodes in front render on top
      const zIndex = Math.round((depthFactor + 1) * 5); // 0 to 10
      
      return { ...link, x, y, angle, scale, zIndex, depthFactor };
    });

    return { center: { x: centerX, y: centerY }, nodes };
  };

  const { center, nodes } = calculatePositions();
  const centerNodeConfig = centerConfig[variant] || centerConfig.analyze;
  const centerNodeColors = centerColors[variant] || centerColors.analyze;
  const CenterIcon = centerNodeConfig.icon;

  return (
    <Box
      ref={containerRef}
      position="relative"
      w="100%"
      h="100%"
      minH="280px"
      bg={containerBg}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      overflow="hidden"
      boxShadow="sm"
    >
      {/* SVG for connecting lines */}
      {containerSize.width > 0 && (
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          {nodes.map((node, index) => {
            const nodeColors = getBubbleColors(node.label);
            return (
              <line
                key={`line-${index}`}
                x1={center.x}
                y1={center.y}
                x2={node.x}
                y2={node.y}
                stroke={nodeColors.border}
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.6"
              />
            );
          })}
        </svg>
      )}

      {/* Center node */}
      {containerSize.width > 0 && (
        <Box
          position="absolute"
          left={`${center.x}px`}
          top={`${center.y}px`}
          transform="translate(-50%, -50%)"
          px={5}
          py={2}
          borderRadius="full"
          bg={centerNodeColors.bg}
          borderWidth="2px"
          borderColor={centerNodeColors.border}
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="0 4px 12px -2px rgba(0,0,0,0.15)"
          zIndex={5}
        >
          <HStack spacing={2}>
            <Icon 
              as={CenterIcon} 
              color={centerNodeColors.icon} 
              boxSize={5}
            />
            <Text
              color={centerNodeColors.text}
              fontSize="md"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              {centerNodeConfig.label}
            </Text>
          </HStack>
        </Box>
      )}

      {/* Dashboard link nodes */}
      {nodes.map((node, index) => {
        const colors = getBubbleColors(node.label);
        const BubbleIcon = getBubbleIcon(node.label);
        const nodeScale = node.scale || 1;
        const nodeZIndex = node.zIndex || 1;
        // Slightly reduce opacity for nodes at the back
        const nodeOpacity = 0.7 + 0.3 * ((node.depthFactor + 1) / 2); // 0.7 to 1.0
        
        return (
          <Box
            key={index}
            position="absolute"
            left={`${node.x}px`}
            top={`${node.y}px`}
            transform={`translate(-50%, -50%) scale(${nodeScale})`}
            px={4}
            py={2}
            borderRadius="full"
            bg={colors.bg}
            borderWidth="1px"
            borderColor={colors.border}
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            boxShadow="0 2px 8px -2px rgba(0,0,0,0.1)"
            opacity={nodeOpacity}
            _hover={{
              boxShadow: '0 4px 12px -2px rgba(0,0,0,0.2)',
              transform: `translate(-50%, -50%) scale(${nodeScale * 1.05})`,
              zIndex: 20,
              opacity: 1,
            }}
            onClick={() => handleBubbleClick(node.path)}
            zIndex={nodeZIndex}
            userSelect="none"
          >
            <HStack spacing={2}>
              <Icon 
                as={BubbleIcon} 
                color={colors.icon} 
                boxSize={3}
              />
              <Text
                color={colors.text}
                fontSize="sm"
                fontWeight="semibold"
                whiteSpace="nowrap"
              >
                {node.label}
              </Text>
            </HStack>
          </Box>
        );
      })}
      
      {/* Empty state */}
      {links.length === 0 && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          h="100%"
          color="gray.400"
          fontSize="sm"
        >
          No dashboard links
        </Box>
      )}
    </Box>
  );
};

export default DashboardLinkBubbles;
